// Push Notification Manager for background notifications

class PushNotificationManager {
  private swRegistration: ServiceWorkerRegistration | null = null
  private isSubscribed: boolean = false

  async init(): Promise<void> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.log('[PushNotification] Push messaging is not supported')
      return
    }

    try {
      // Register service worker
      this.swRegistration = await navigator.serviceWorker.register('/service-worker.js')
      console.log('[PushNotification] Service Worker registered:', this.swRegistration)

      // Check if already subscribed
      const subscription = await this.swRegistration.pushManager.getSubscription()
      this.isSubscribed = subscription !== null

      if (this.isSubscribed) {
        console.log('[PushNotification] Already subscribed to push notifications')
      }
    } catch (error) {
      console.error('[PushNotification] Service Worker registration failed:', error)
    }
  }

  async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.log('[PushNotification] Notifications not supported')
      return false
    }

    const permission = await Notification.requestPermission()

    if (permission === 'granted') {
      console.log('[PushNotification] Permission granted')
      return true
    } else {
      console.log('[PushNotification] Permission denied')
      return false
    }
  }

  async subscribe(): Promise<PushSubscription | null> {
    if (!this.swRegistration) {
      console.log('[PushNotification] Service Worker not registered')
      return null
    }

    // Request permission first
    const hasPermission = await this.requestPermission()
    if (!hasPermission) {
      return null
    }

    try {
      // Get VAPID public key from server
      const response = await fetch('/push/vapid_public_key')
      const { public_key } = await response.json()

      if (!public_key) {
        console.log('[PushNotification] No VAPID key available')
        return null
      }

      // Convert base64 to Uint8Array
      const applicationServerKey = this.urlBase64ToUint8Array(public_key)

      // Subscribe to push
      const subscription = await this.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey
      })

      console.log('[PushNotification] Subscribed:', subscription)

      // Send subscription to server
      await this.sendSubscriptionToServer(subscription)

      this.isSubscribed = true
      return subscription
    } catch (error) {
      console.error('[PushNotification] Failed to subscribe:', error)
      return null
    }
  }

  async unsubscribe(): Promise<boolean> {
    if (!this.swRegistration) {
      return false
    }

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription()

      if (subscription) {
        // Remove from server
        await this.removeSubscriptionFromServer(subscription)

        // Unsubscribe
        await subscription.unsubscribe()
        console.log('[PushNotification] Unsubscribed')
      }

      this.isSubscribed = false
      return true
    } catch (error) {
      console.error('[PushNotification] Failed to unsubscribe:', error)
      return false
    }
  }

  private async sendSubscriptionToServer(subscription: PushSubscription): Promise<void> {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

    await fetch('/push/subscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        subscription: subscription.toJSON()
      })
    })
  }

  private async removeSubscriptionFromServer(subscription: PushSubscription): Promise<void> {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

    await fetch('/push/unsubscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint
      })
    })
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/')

    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }

    return outputArray
  }

  getIsSubscribed(): boolean {
    return this.isSubscribed
  }

  // Show a local notification (for testing)
  async showLocalNotification(title: string, options: NotificationOptions = {}): Promise<void> {
    if (!this.swRegistration) {
      return
    }

    const defaultOptions: NotificationOptions = {
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      vibrate: [200, 100, 200],
      requireInteraction: true,
      ...options
    }

    await this.swRegistration.showNotification(title, defaultOptions)
  }
}

// Global instance
const pushNotificationManager = new PushNotificationManager()

// Initialize on page load
function initPushNotifications() {
  const userMeta = document.querySelector('meta[name="current-user-id"]')
  if (userMeta) {
    pushNotificationManager.init()
  }
}

// Make accessible globally
;(window as any).pushNotificationManager = pushNotificationManager

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPushNotifications)
} else {
  initPushNotifications()
}

document.addEventListener('turbo:load', initPushNotifications)

export default pushNotificationManager
